import { useState } from 'react';

import { Button, Flex, InputField } from '@raystack/apsara';
import { For } from '@studio/hoc';

import { getLatLngInWsg84, isValidWgs84Range } from '../utils/common';

import styles from './styles.module.css';

type ButtonProps = React.ComponentProps<typeof Button>;

interface Actions {
  label: string;
  variant: ButtonProps['variant'];
  color: ButtonProps['color'];
  onClick: ButtonProps['onClick'];
  disabled: ButtonProps['disabled'];
}

export interface AddPointProps {
  onDiscard: VoidFunction;
  onCreate: (coords: [number, number], radius: number) => void;
  defaultCoords: [number, number];
  defaultRadius: number;
  maxRadius: number;
  minRadius: number;
}

export const AddPoint = ({
  onDiscard,
  onCreate,
  defaultCoords,
  defaultRadius,
  maxRadius,
  minRadius
}: AddPointProps) => {
  const [options, setOptions] = useState([
    {
      id: 'coords',
      label: 'Coordinate',
      value: defaultCoords.join(', '),
      validation: (value: string) => {
        const wsg84 = getLatLngInWsg84(value);

        return !(
          wsg84.isValid &&
          isValidWgs84Range(wsg84.getLatLng() as [number, number])
        );
      },
      hasError: false,
      errorMsg: `Invalid coordinate`
    },
    {
      id: 'radius',
      label: 'Radius (km)',
      value: defaultRadius,
      validation: (value: string | number) => {
        const num = Number(value);
        return num < minRadius || num > maxRadius;
      },
      hasError: false,
      errorMsg: ''
    }
  ]);

  const handleOnCreate = () =>
    onCreate(
      (options[0].value as string).split(', ').map(Number) as [number, number],
      Number(options[1].value)
    );

  const actions: Actions[] = [
    {
      label: 'Discard',
      variant: 'outline',
      color: 'neutral',
      onClick: onDiscard,
      disabled: false
    },
    {
      label: 'Create',
      variant: 'solid',
      color: 'accent',
      onClick: handleOnCreate,
      disabled: options.some(d => d.hasError)
    }
  ];

  function handleChange(e: React.ChangeEvent<HTMLInputElement>, id: string) {
    const idx = options.findIndex(d => d.id === id);
    const { value } = e.target;

    if (idx > -1) {
      options[idx].value = value;

      if (options[idx].id === 'radius') {
        const num = Number(value);
        if (num < minRadius) {
          options[idx].hasError = true;
          options[idx].errorMsg = 'Invalid Radius';
        } else if (num > maxRadius) {
          options[idx].hasError = true;
          options[idx].errorMsg = `Max allowed radius is ${maxRadius} km`;
        } else {
          options[idx].hasError = false;
          options[idx].errorMsg = '';
        }
      } else {
        if (options[idx].validation(value)) {
          options[idx].hasError = true;
        } else {
          options[idx].hasError = false;
        }
      }

      setOptions([...options]);
    }
  }

  return (
    <Flex direction='column' className={styles.addPoint}>
      <For each={options}>
        {option => {
          return (
            <Flex direction='column' gap='small'>
              <InputField
                label={option.label}
                value={option.value}
                onChange={e => handleChange(e, option.id)}
                data-test-id={`add-point-${option.id}`}
                error={option.hasError ? option.errorMsg : undefined}
              />
            </Flex>
          );
        }}
      </For>

      <Flex justify='between' gap='small' className={styles.btnContainer}>
        <For each={actions}>
          {action => (
            <Button
              width='100%'
              variant={action.variant}
              color={action.color}
              disabled={action.disabled}
              onClick={action.onClick}
              data-test-id={action.label}
            >
              {action.label}
            </Button>
          )}
        </For>
      </Flex>
    </Flex>
  );
};
